import { useEffect, useState } from 'react'
import { Sheet } from '../ui/Sheet'
import { LegalSheet } from '../legal/LegalSheet'
import { DOCS, type Doc } from '../legal/documents'
import { buyPlus, platform, plusOffer, type PlusOffer, type PlusOfferFailure, type PurchaseOutcome } from './checkout'

/**
 * The paywall — the screen a purchase starts from, and therefore the screen App
 * Review reads for guideline 3.1.2 and Schedule 2 of the PLA: what Plus is, what it
 * costs PER PERIOD in the buyer's own currency (read from the store's offering, never
 * typed here), that it renews until cancelled and how to cancel, and working links
 * to the privacy policy and the terms.
 *
 * Both doors to a purchase — the Voice row's Upgrade and the quota sheet's — open
 * THIS screen, so the disclosure cannot be skipped by coming in through the other
 * door. The store's own sheet (StoreKit's, or RevenueCat's web checkout) opens from
 * the Subscribe button here, after the person has had the terms in front of them.
 */
export function PlusSheet({ onClose, onDone }: { onClose: () => void; onDone: (outcome: PurchaseOutcome) => void }) {
  // undefined = still asking the store; null = this build/platform has nothing to sell;
  // a failure = the store was asked and did not answer, and the sheet says why.
  const [offer, setOffer] = useState<PlusOffer | PlusOfferFailure | null | undefined>(undefined)
  const [busy, setBusy] = useState(false)
  const [note, setNote] = useState<string | null>(null)
  const [doc, setDoc] = useState<Doc | null>(null)
  const ready: PlusOffer | null = offer && 'price' in offer ? offer : null
  const failed: PlusOfferFailure | null = offer && 'failure' in offer ? offer : null

  // Asked on open and again on "Try again"; an answer that arrives after the sheet
  // closed (or after a newer ask) is dropped.
  const [ask, setAsk] = useState(0)
  useEffect(() => {
    let live = true
    setOffer(undefined)
    void plusOffer().then((o) => {
      if (live) setOffer(o)
    })
    return () => {
      live = false
    }
  }, [ask])

  const subscribe = async () => {
    setBusy(true)
    setNote(null)
    const r = await buyPlus()
    setBusy(false)
    if (r === 'purchased') {
      onDone(r)
      return
    }
    if (r === 'cancelled') return
    setNote(r === 'unavailable' ? 'Plus is not available on this build yet.' : 'The purchase did not go through. Try again.')
  }

  const period = ready?.period ?? 'month'
  const plat = platform()
  const privacy = DOCS.find((d) => d.id === 'privacy')
  const terms = DOCS.find((d) => d.id === 'terms')

  return (
    <>
      <Sheet
        title="Avengents Plus"
        onClose={onClose}
        className="plus-sheet"
        actions={
          <>
            <button type="button" className="sheet-btn" onClick={onClose} disabled={busy}>
              Not now
            </button>
            <button type="button" className="sheet-btn primary" onClick={() => void subscribe()} disabled={busy || !ready}>
              {busy ? 'Opening…' : ready ? `Subscribe · ${ready.price} / ${period}` : 'Subscribe'}
            </button>
          </>
        }
      >
        <p className="plus-what">
          An hour of voice input every day, counted from 00:00 UTC. Without Plus, voice input is limited.
        </p>
        <p className="plus-price" aria-live="polite">
          {offer === undefined
            ? 'Asking the store for the price…'
            : ready
              ? `${ready.price} per ${period}`
              : failed
                ? `The store did not answer — ${failed.failure}`
                : 'Not available on this build yet.'}
        </p>
        {failed && (
          <p className="plus-links">
            <button type="button" className="plus-link" onClick={() => setAsk((n) => n + 1)}>
              Try again
            </button>
          </p>
        )}
        <p className="plus-terms">{renewalTerms(plat, period)}</p>
        <p className="plus-links">
          {privacy && (
            <button type="button" className="plus-link" onClick={() => setDoc(privacy)}>
              {privacy.title}
            </button>
          )}
          {privacy && terms && ' · '}
          {terms && (
            <button type="button" className="plus-link" onClick={() => setDoc(terms)}>
              {terms.title}
            </button>
          )}
        </p>
        {note && <p className="gh-note plus-note">{note}</p>}
      </Sheet>
      {doc && <LegalSheet doc={doc} onClose={() => setDoc(null)} />}
    </>
  )
}

/**
 * The renewal terms, in the words of the store that will charge: who is charged,
 * that it renews until cancelled, the 24-hour rule, and WHERE to cancel — the place
 * differs per till, and "cancel in your settings" without a place is a door with no
 * handle (the same rule as the quota sheet's reset time).
 */
function renewalTerms(plat: string, period: string): string {
  const [charged, where] =
    plat === 'ios'
      ? ['your Apple Account', 'Settings › Apple Account › Subscriptions']
      : plat === 'android'
        ? ['your Google Play account', 'Google Play › Subscriptions']
        : ['your card', 'the Manage button in this menu']
  return (
    `Charged to ${charged} when you confirm. Renews automatically every ${period} until you cancel; ` +
    `cancel at least 24 hours before the ${period} ends to stop the next charge, in ${where}. ` +
    `Cancelling does not shorten the ${period} you have already paid for.`
  )
}
